(function () {
  const config = (window.themeVariables && window.themeVariables.wishlist) || {};
  const strings = config.strings || {};

  const state = {
    swat: null,
    lid: null,
    keys: new Set(), // "empi:epi"
    items: [], // raw Swym listcontents
    ready: false,
    loggedIn: !!config.isLoggedIn
  };

  const key = (empi, epi) => `${empi}:${epi}`;

  function sharedListPath(lid) {
    const root = ((window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/').replace(/\/$/, '');
    return `${root}${config.pageUrl || '/pages/wishlist'}?lid=${encodeURIComponent(lid)}`;
  }

  // redirect legacy Swym share links to our own page
  if (/\/pages\/swym-share-wishlist\/?$/.test(window.location.pathname)) {
    const params = new URLSearchParams(window.location.search);
    const legacyLid = params.get('lid') || params.get('hkey');
    if (legacyLid) window.location.replace(sharedListPath(legacyLid));
  }

  // guests get a cookie-based Swym session up to this many items, then must log in
  const GUEST_ITEM_LIMIT = 5;

  function hasAnyVariant(empi) {
    const prefix = `${empi}:`;
    for (const k of state.keys) {
      if (k.startsWith(prefix)) return true;
    }
    return false;
  }

  // lty is null or "wl"; anything else isn't the wishlist.
  function isWishlist(list) {
    const type = list.lty == null ? '' : String(list.lty).toLowerCase();
    return type === '' || type === 'wl';
  }

  // For instant paint before fetchLists resolves.
  const CACHE_KEY = 'wishlist:items:v1';

  function writeCache() {
    try {
      sessionStorage.setItem(CACHE_KEY, JSON.stringify({
        customerId: config.customerId || null,
        items: state.items
      }));
    } catch (e) {
      // best-effort
    }
  }

  function readCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null');
      if (!cached || cached.customerId !== (config.customerId || null)) return null;
      return cached.items || null;
    } catch (e) {
      return null;
    }
  }

  const CARD_CACHE_KEY = 'wishlist:cards:v5';
  const CARD_CACHE_LIMIT = 24;

  function cardUrl(item) {
    const path = new URL(item.du, window.location.origin).pathname;
    return `${path}?view=wishlist-card&variant=${encodeURIComponent(item.epi)}`;
  }

  function readCardCache() {
    try {
      const cached = JSON.parse(sessionStorage.getItem(CARD_CACHE_KEY) || 'null');
      if (!cached || cached.customerId !== (config.customerId || null)) return {};
      return cached.cards || {};
    } catch (e) {
      return {};
    }
  }

  function writeCardCache(cards) {
    // Oldest-first trim (insertion order)
    const keys = Object.keys(cards);
    const trimmed = {};
    keys.slice(Math.max(0, keys.length - CARD_CACHE_LIMIT)).forEach(k => { trimmed[k] = cards[k]; });

    try {
      sessionStorage.setItem(CARD_CACHE_KEY, JSON.stringify({
        customerId: config.customerId || null,
        cards: trimmed
      }));
    } catch (e) {
      // Quota exceeded: halve and retry once.
      try {
        const half = {};
        const ks = Object.keys(trimmed);
        ks.slice(Math.floor(ks.length / 2)).forEach(k => { half[k] = trimmed[k]; });
        sessionStorage.setItem(CARD_CACHE_KEY, JSON.stringify({
          customerId: config.customerId || null,
          cards: half
        }));
      } catch (e2) {
        // best-effort
      }
    }
  }

  function getCachedCard(item) {
    return readCardCache()[key(item.empi, item.epi)] || null;
  }

  function putCachedCard(item, html) {
    const cards = readCardCache();
    cards[key(item.empi, item.epi)] = html;
    writeCardCache(cards);
  }

  function pruneCardCache() {
    const cards = readCardCache();
    let changed = false;

    Object.keys(cards).forEach(k => {
      if (!state.keys.has(k)) {
        delete cards[k];
        changed = true;
      }
    });

    if (changed) writeCardCache(cards);
  }

  function fetchCardHtml(item) {
    return fetch(cardUrl(item))
      .then(response => (response.ok ? response.text() : null))
      .catch(() => null);
  }

  function prefetchCards() {
    if (window.location.pathname === (config.pageUrl || '/pages/wishlist')) return;

    const cached = readCardCache();
    const missing = state.items
      .filter(item => !cached[key(item.empi, item.epi)])
      .slice(0, CARD_CACHE_LIMIT);

    if (!missing.length) return;

    const run = () => {
      missing.forEach(item => {
        fetchCardHtml(item).then(html => {
          if (html) putCachedCard(item, html);
        });
      });
    };

    if (typeof requestIdleCallback === 'function') {
      requestIdleCallback(run, { timeout: 4000 });
    } else {
      setTimeout(run, 1500);
    }
  }

  function emit() {
    writeCache();

    document.dispatchEvent(new CustomEvent('wishlist:change', {
      detail: { count: state.keys.size, keys: state.keys, items: state.items, ready: state.ready }
    }));
  }

  function loginRedirect() {
    const url = config.loginUrl || '/account/login';
    window.location.href = url + '?return_url=' + encodeURIComponent(window.location.pathname + window.location.search);
  }

  // ---------------------------------------------------------------------------
  // Swym bridge
  // ---------------------------------------------------------------------------

  function refreshFromSwym(done) {
    if (!state.swat) return;

    state.swat.fetchLists({
      callbackFn: function (lists) {
        const list = (lists || []).filter(isWishlist)[0] || null;

        if (!list && (lists || []).length) {
          console.warn('[wishlist] no default list matched; lists were', lists);
        }

        state.lid = list ? list.lid : null;
        state.items = (list && list.listcontents) || [];
        state.keys = new Set();
        state.items.forEach(item => state.keys.add(key(item.empi, item.epi)));
        state.ready = true;
        emit();
        pruneCardCache();
        prefetchCards();
        if (done) done(list);
      },
      errorFn: function (error) {
        console.error('[wishlist] fetchLists failed', error);
        state.ready = true;
        emit();
        if (done) done(null);
      }
    });
  }

  // Callers must revert optimistic state on error.
  function ensureList(callback) {
    if (state.lid) return callback(null);

    state.swat.createList({ lname: 'Wishlist' }, function (list) {
      state.lid = list.lid;
      callback(null);
    }, function (error) {
      console.error('[wishlist] createList failed', error);
      callback(error || new Error('createList failed'));
    });
  }

  function add(product, onDone) {
    if (!state.loggedIn && state.keys.size >= GUEST_ITEM_LIMIT) {
      loginRedirect();
      return onDone && onDone(false);
    }

    ensureList(function (listError) {
      if (listError) return onDone && onDone(false);

      state.swat.addToList(state.lid, product, function (addedItem) {
        state.keys.add(key(product.empi, product.epi));
        state.items = state.items.concat([addedItem || product]);
        emit();
        if (onDone) onDone(true);
      }, function (error) {
        console.error('[wishlist] addToList failed', error);
        if (onDone) onDone(false);
      });
    });
  }

  function remove(product, onDone) {
    if (!state.lid) return onDone && onDone(false);

    state.swat.deleteFromList(state.lid, product, function () {
      state.keys.delete(key(product.empi, product.epi));
      state.items = state.items.filter(i => key(i.empi, i.epi) !== key(product.empi, product.epi));
      emit();
      if (onDone) onDone(true);
    }, function (error) {
      console.error('[wishlist] deleteFromList failed', error);
      if (onDone) onDone(false);
    });
  }

  // don't use Swym's hosted share URL, it renders nothing in SDK-only mode
  function getShareUrl(onSuccess, onError) {
    if (!state.lid) return onError(new Error('Wishlist is empty'));

    state.swat.markListPublic(state.lid, function () {
      onSuccess(window.location.origin + sharedListPath(state.lid));
    }, onError);
  }

  function fetchSharedList(lid, onSuccess, onError) {
    const run = function (swat) {
      swat.fetchListDetails({ lid: lid }, function (response) {
        const items = response && (response.items || response.listcontents || (response.list && response.list.listcontents));
        onSuccess(Array.isArray(items) ? items : []);
      }, onError);
    };

    state.swat ? run(state.swat) : window.SwymCallbacks.push(run);
  }

  function removeAllVariants(empi, du, onDone) {
    const prefix = `${empi}:`;
    const epis = [];
    state.keys.forEach(k => {
      if (k.startsWith(prefix)) epis.push(parseInt(k.slice(prefix.length), 10));
    });

    if (!epis.length) return onDone && onDone(true);

    let remaining = epis.length;
    let allOk = true;
    epis.forEach(epi => {
      remove({ empi: parseInt(empi, 10), epi, du }, (ok) => {
        if (!ok) allOk = false;
        remaining -= 1;
        if (remaining === 0 && onDone) onDone(allOk);
      });
    });
  }

  window.SwymCallbacks = window.SwymCallbacks || [];
  window.SwymCallbacks.push(function (swat) {
    state.swat = swat;

    try {
      state.loggedIn = swat.platform.isLoggedIn();
    } catch (e) {
      // Fall back to the Liquid-rendered value.
    }

    refreshFromSwym();

    if (swat.evtLayer) {
      swat.evtLayer.addEventListener('sw:customerInfoRefreshed', function () {
        refreshFromSwym();
      });
    }
  });

  window.themeWishlist = {
    state: state,
    refresh: refreshFromSwym,
    add: add,
    remove: remove,
    getShareUrl: getShareUrl,
    fetchSharedList: fetchSharedList,
    isWishlisted: (empi, epi) => state.keys.has(key(empi, epi)),
    cachedItems: readCache,
    getCachedCard: getCachedCard,
    putCachedCard: putCachedCard,
    fetchCardHtml: fetchCardHtml,
    loginRedirect: loginRedirect
  };

  // ---------------------------------------------------------------------------
  // <wishlist-button>
  // ---------------------------------------------------------------------------

  if (!customElements.get('wishlist-button')) {
    customElements.define('wishlist-button', class extends HTMLElement {
      connectedCallback() {
        this._button = this.querySelector('button');
        this._label = this.querySelector('[data-wishlist-label]');
        this._picker = this.parentElement?.querySelector('[data-wishlist-size-picker]') || null;
        this._notice = this._picker ? null : (this.parentElement?.querySelector('[data-wishlist-notice]') || null);
        this._empi = this.dataset.empi;
        this._epi = this.dataset.epi;
        this._url = this.dataset.url;

        // re-read live variant on reconnect so color changes don't reset the heart
        if (this.dataset.context !== 'card') {
          const liveForm = (this.dataset.formId && document.forms[this.dataset.formId]) || this.closest('form');
          const liveVariantId = liveForm?.querySelector('[name="id"]')?.value;
          if (liveVariantId) this._epi = liveVariantId;
        }

        this._onChange = () => this.render();
        document.addEventListener('wishlist:change', this._onChange);

        this._button.addEventListener('click', this._onClick.bind(this));

        if (this._picker) {
          this._picker.addEventListener('click', this._onPickerClick.bind(this));
          this._outsideClick = (event) => {
            if (this.contains(event.target) || this._picker.contains(event.target)) return;
            this._closePicker();
          };
          this._sizeItems = this._picker.querySelector('[data-wishlist-size-items]');
          if (this._sizeItems) {
            this._onSizeScroll = () => this._updateSizeArrows();
            this._sizeItems.addEventListener('scroll', this._onSizeScroll);
          }
        }

        this._bindVariantSync();
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
        if (this._outsideClick) document.removeEventListener('click', this._outsideClick, true);
        if (this._sizeItems && this._onSizeScroll) this._sizeItems.removeEventListener('scroll', this._onSizeScroll);
        clearTimeout(this._confirmTimer);
        if (this._form && this._onFormChange) {
          this._form.removeEventListener('change', this._onFormChange);
          this._form.removeEventListener('click', this._onFormChange);
          this._form.removeEventListener('variant:change', this._onFormChange);
        }
      }

      _openPicker() {
        const figure = this.closest('.product-card__figure');
        if (figure) {
          figure.classList.remove('is-sizes-open');
          figure.classList.add('is-wishlist-sizes-open');
        }
        this.classList.add('is-picker-open');
        setTimeout(() => document.addEventListener('click', this._outsideClick, true), 0);
        if (this._picker) this._picker.classList.remove('is-confirmed');
        this._paintPicker();
        this._updateSizeArrows();
      }

      _updateSizeArrows() {
        const items = this._sizeItems;
        if (!items) return;
        const prev = this._picker.querySelector('[data-wishlist-size-prev]');
        const next = this._picker.querySelector('[data-wishlist-size-next]');
        const overflowing = items.scrollWidth > items.clientWidth + 2;
        if (prev) prev.classList.toggle('is-visible', overflowing && items.scrollLeft > 2);
        if (next) next.classList.toggle('is-visible', overflowing && items.scrollLeft < items.scrollWidth - items.clientWidth - 2);
      }

      _closePicker() {
        this.closest('.product-card__figure')?.classList.remove('is-wishlist-sizes-open');
        this.classList.remove('is-picker-open');
        document.removeEventListener('click', this._outsideClick, true);
        clearTimeout(this._confirmTimer);
      }

      _showAddedConfirmation() {
        if (!this._picker) return;
        this._picker.classList.add('is-confirmed');
        clearTimeout(this._confirmTimer);
        this._confirmTimer = setTimeout(() => this._closePicker(), 1000);
      }

      _showNotice() {
        if (!this._notice) return;
        const figure = this.closest('.product-card__figure');
        clearTimeout(this._confirmTimer);
        figure?.classList.remove('is-sizes-open');
        figure?.classList.add('is-wishlist-sizes-open');
        this._notice.classList.add('is-confirmed');
        this._confirmTimer = setTimeout(() => this._hideNotice(), 1000);
      }

      _hideNotice() {
        if (!this._notice) return;
        clearTimeout(this._confirmTimer);
        this.closest('.product-card__figure')?.classList.remove('is-wishlist-sizes-open');
        this._confirmTimer = setTimeout(() => this._notice.classList.remove('is-confirmed'), 250);
      }

      _paintPicker() {
        this._picker.querySelectorAll('[data-variant-id]').forEach(chip => {
          chip.classList.toggle('is-selected', state.keys.has(key(this._empi, chip.dataset.variantId)));
        });
      }

      _onPickerClick(event) {
        event.preventDefault();
        event.stopPropagation();

        const scrollBtn = event.target.closest('[data-wishlist-size-prev], [data-wishlist-size-next]');
        if (scrollBtn) {
          if (this._sizeItems) {
            this._sizeItems.scrollBy({ left: scrollBtn.hasAttribute('data-wishlist-size-next') ? 200 : -200, behavior: 'smooth' });
          }
          return;
        }

        const chip = event.target.closest('[data-variant-id]');
        if (!chip || chip.classList.contains('is-unavailable')) return;

        const empi = parseInt(this._empi, 10);
        const epi = parseInt(chip.dataset.variantId, 10);
        const du = new URL(this._url, window.location.origin).href;
        const wasSaved = state.keys.has(key(empi, epi));

        chip.classList.toggle('is-selected', !wasSaved);
        if (!wasSaved) this._showAddedConfirmation();
        this.classList.add('is-loading');

        const done = (ok) => {
          this.classList.remove('is-loading');
          if (ok) return this.render();

          this._paintPicker();
          if (!wasSaved) {
            clearTimeout(this._confirmTimer);
            if (this._picker) this._picker.classList.remove('is-confirmed');
          }
        };

        wasSaved
          ? remove({ empi, epi, du }, done)
          : add({ empi, epi, du }, done);
      }

      _bindVariantSync() {
        if (this.dataset.context === 'card') {
          const card = this.closest('product-card');
          if (!card) return;

          this._onFormChange = (event) => {
            const swatch = event.target.closest
              ? event.target.closest('.product-card__tooltip-swatch[data-variant-id], [type="radio"][data-variant-id]')
              : null;
            if (!swatch) return;
            this._applySwatch(card, swatch);
          };

          this._form = card;
          card.addEventListener('click', this._onFormChange);
          card.addEventListener('change', this._onFormChange);
        } else {
          const form = (this.dataset.formId && document.forms[this.dataset.formId]) ||
            this.closest('form') ||
            this.closest('.shopify-section')?.querySelector('form[action*="/cart/add"]');
          if (!form) return;

          this._onFormChange = (event) => {
            const variantId = event.detail?.variant?.id || form.querySelector('[name="id"]')?.value;
            if (variantId) {
              this._epi = variantId;
              this.render();
            }
          };
          this._form = form;
          form.addEventListener('variant:change', this._onFormChange);
          form.addEventListener('change', this._onFormChange);
        }
      }

      _applySwatch(card, swatch) {
        if (this._picker) this._closePicker();

        this._epi = swatch.dataset.variantId;

        const productUrl = swatch.dataset.productUrl;
        if (productUrl) this._url = productUrl;

        const handle = productUrl && productUrl.split('/products/')[1];
        const isCombined = card.dataset.combinedListing === 'true';

        if (!isCombined || !handle) return this.render();

        const cleanHandle = handle.split('?')[0].split('/')[0];
        if (cleanHandle === card.getAttribute('handle')) {
          this._empi = this.dataset.empi;
          return this.render();
        }

        const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const cached = window._swatchPrefetchCache && window._swatchPrefetchCache.get(cleanHandle);
        const request = cached || fetch(`${root}products/${cleanHandle}.js`).then(r => r.json());

        const token = (this._swatchToken = (this._swatchToken || 0) + 1);
        request.then(product => {
          if (!product || token !== this._swatchToken) return;
          this._empi = product.id;
          this.render();
        }).catch(() => {});
      }

      _product() {
        return {
          empi: parseInt(this._empi, 10),
          epi: parseInt(this._epi, 10),
          du: new URL(this._url, window.location.origin).href
        };
      }

      _onClick(event) {
        event.preventDefault();
        event.stopPropagation();

        if (!state.ready) return;

        if (this._picker) {
          if (hasAnyVariant(this._empi)) {
            const du = new URL(this._url, window.location.origin).href;

            this.classList.remove('is-active');
            this._paint(false);
            this.classList.add('is-loading');
            this._closePicker();

            removeAllVariants(this._empi, du, (ok) => {
              this.classList.remove('is-loading');
              if (!ok) this.render();
            });
            return;
          }

          this.classList.contains('is-picker-open') ? this._closePicker() : this._openPicker();
          return;
        }

        const active = this.classList.contains('is-active');
        const product = this._product();

        this.classList.toggle('is-active', !active);
        this._paint(!active);
        this.classList.add('is-loading');
        if (!active) this._showNotice();

        const done = (ok) => {
          this.classList.remove('is-loading');
          if (ok) return;
          this.render();
          if (!active) this._hideNotice();
        };

        active ? remove(product, done) : add(product, done);
      }

      render() {
        if (!this._button) return;
        this.classList.toggle('is-pending', !state.ready);
        const active = this._picker
          ? hasAnyVariant(this._empi)
          : state.keys.has(key(this._empi, this._epi));
        this.classList.toggle('is-active', active);
        this._paint(active);
        if (this._picker) this._paintPicker();
      }

      _paint(active) {
        const label = active ? (strings.remove || 'Remove from wishlist') : (strings.add || 'Add to wishlist');
        this._button.setAttribute('aria-pressed', active ? 'true' : 'false');
        this._button.setAttribute('aria-label', label);
        if (this._label) {
          this._label.textContent = active ? (strings.added || 'Saved') : (strings.add || 'Add to wishlist');
        }
      }
    });
  }

  // ---------------------------------------------------------------------------
  // <wishlist-count> / <wishlist-dot> — mirror the theme's CartCount / CartDot
  // ---------------------------------------------------------------------------

  if (!customElements.get('wishlist-count')) {
    customElements.define('wishlist-count', class extends HTMLElement {
      connectedCallback() {
        this._onChange = (event) => { this.innerText = event.detail.count; };
        document.addEventListener('wishlist:change', this._onChange);
        this.innerText = state.keys.size;
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
      }
    });
  }

  if (!customElements.get('wishlist-dot')) {
    customElements.define('wishlist-dot', class extends HTMLElement {
      connectedCallback() {
        this._onChange = (event) => { this.classList.toggle('is-visible', event.detail.count > 0); };
        document.addEventListener('wishlist:change', this._onChange);
        this.classList.toggle('is-visible', state.keys.size > 0);
      }
      disconnectedCallback() {
        document.removeEventListener('wishlist:change', this._onChange);
      }
    });
  }
})();
